import { ENEMY_CATALOG, bossKindForStage, isBossStage } from '../entities/enemyCatalog';
import { GameState } from '../types';

export interface DevMenuHost {
  getGameState(): GameState;
  getCurrentStage(): number;
  skipToStage(stage: number): boolean;
  requestPointerLock(): void;
  exitPointerLock(): void;
}

/**
 * Backtick-toggled developer panel for jumping to stages / boss arenas.
 */
export class DevMenu {
  private readonly root: HTMLDivElement;
  private readonly host: DevMenuHost;
  private open = false;
  private readonly stageInput: HTMLInputElement;
  private readonly statusEl: HTMLParagraphElement;

  constructor(parent: HTMLElement, host: DevMenuHost) {
    this.host = host;

    this.root = document.createElement('div');
    this.root.id = 'dev-overlay';
    this.root.innerHTML = `
      <div class="dev-panel" role="dialog" aria-label="Dev Menu">
        <header class="dev-header">
          <h2>Dev Menu</h2>
          <span class="dev-hint">\` to close</span>
        </header>
        <p class="dev-status" data-status></p>
        <section class="dev-section">
          <h3>Jump to stage</h3>
          <div class="dev-row">
            <input type="number" min="1" max="999" value="1" data-stage-input />
            <button type="button" data-go>Go</button>
          </div>
        </section>
        <section class="dev-section">
          <h3>Boss stages</h3>
          <div class="dev-boss-grid" data-boss-grid></div>
        </section>
        <section class="dev-section">
          <h3>Quick jumps</h3>
          <div class="dev-row wrap">
            <button type="button" data-jump="1">Stage 1</button>
            <button type="button" data-jump="4">Pre-boss (4)</button>
            <button type="button" data-jump="5">Boss 1 (5)</button>
            <button type="button" data-jump="10">Boss 2 (10)</button>
            <button type="button" data-jump="15">Boss 3 (15)</button>
          </div>
        </section>
      </div>
    `;
    parent.appendChild(this.root);

    this.stageInput = this.root.querySelector('[data-stage-input]') as HTMLInputElement;
    this.statusEl = this.root.querySelector('[data-status]') as HTMLParagraphElement;

    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.setOpen(false);
    });

    this.root.querySelector('[data-go]')!.addEventListener('click', () => {
      this.jump(Number(this.stageInput.value));
    });

    this.stageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.jump(Number(this.stageInput.value));
      }
      e.stopPropagation();
    });

    this.root.querySelectorAll('[data-jump]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const stage = Number((btn as HTMLElement).dataset.jump);
        this.jump(stage);
      });
    });

    this.buildBossButtons();
    this.injectStyles();
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  setOpen(open: boolean): void {
    if (this.open === open) return;
    this.open = open;
    this.root.classList.toggle('open', open);
    if (open) {
      this.host.exitPointerLock();
      this.refresh();
      this.stageInput.focus();
      this.stageInput.select();
    } else if (this.host.getGameState() === GameState.Diving) {
      this.host.requestPointerLock();
    }
  }

  refresh(): void {
    const stage = this.host.getCurrentStage();
    this.stageInput.value = String(Math.max(1, stage));
    let line = `Current stage: ${Math.max(1, stage)}`;
    if (isBossStage(stage)) {
      line += ` · ${ENEMY_CATALOG[bossKindForStage(stage)].name}`;
    }
    this.statusEl.textContent = line;
  }

  dispose(): void {
    this.root.remove();
  }

  private jump(stage: number): void {
    const n = Math.floor(stage);
    if (!Number.isFinite(n) || n < 1) {
      this.statusEl.textContent = 'Enter a stage ≥ 1';
      return;
    }
    const ok = this.host.skipToStage(n);
    if (!ok) {
      this.statusEl.textContent = 'Skip failed (clients cannot skip — host only)';
      return;
    }
    this.refresh();
    this.statusEl.textContent = `Jumped to stage ${n}${
      isBossStage(n) ? ` · ${ENEMY_CATALOG[bossKindForStage(n)].name}` : ''
    }`;
  }

  private buildBossButtons(): void {
    const grid = this.root.querySelector('[data-boss-grid]') as HTMLElement;
    const bosses = [
      { stage: 5, label: 'Vault Sentinel' },
      { stage: 10, label: 'Spire Mother' },
      { stage: 15, label: 'Tunnel Horror' },
      { stage: 20, label: 'Sentinel (20)' },
    ];
    for (const b of bosses) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = `${b.label} · ${b.stage}`;
      btn.addEventListener('click', () => this.jump(b.stage));
      grid.appendChild(btn);
    }
  }

  private injectStyles(): void {
    if (document.getElementById('dev-menu-styles')) return;
    const style = document.createElement('style');
    style.id = 'dev-menu-styles';
    style.textContent = `
      #dev-overlay {
        position: fixed;
        inset: 0;
        z-index: 60;
        display: none;
        align-items: flex-start;
        justify-content: flex-end;
        padding: 16px;
        background: rgba(0, 0, 0, 0.35);
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      }
      #dev-overlay.open { display: flex; }
      .dev-panel {
        width: min(360px, 100%);
        max-height: calc(100vh - 32px);
        overflow: auto;
        background: #1a1e24;
        color: #e8ecf0;
        border: 1px solid #3a4555;
        border-radius: 8px;
        padding: 14px 16px 16px;
        box-shadow: 0 12px 40px rgba(0,0,0,0.45);
      }
      .dev-header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 8px;
      }
      .dev-header h2 {
        margin: 0;
        font-size: 15px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #9fd4ff;
      }
      .dev-hint { font-size: 11px; color: #8899aa; }
      .dev-status {
        margin: 0 0 12px;
        font-size: 12px;
        color: #b8c4d0;
        min-height: 1.2em;
      }
      .dev-section { margin-bottom: 14px; }
      .dev-section h3 {
        margin: 0 0 8px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #7a8a9a;
      }
      .dev-row {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      .dev-row.wrap { flex-wrap: wrap; }
      .dev-row input[type="number"] {
        flex: 1;
        min-width: 0;
        background: #0e1218;
        border: 1px solid #3a4555;
        color: #e8ecf0;
        border-radius: 4px;
        padding: 6px 8px;
        font: inherit;
        font-size: 13px;
      }
      .dev-panel button {
        background: #2a3544;
        border: 1px solid #4a5a6a;
        color: #e8ecf0;
        border-radius: 4px;
        padding: 6px 10px;
        font: inherit;
        font-size: 12px;
        cursor: pointer;
      }
      .dev-panel button:hover {
        background: #3a4a5c;
        border-color: #6a8aaa;
      }
      .dev-boss-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
      }
      .dev-boss-grid button {
        text-align: left;
        font-size: 11px;
      }
    `;
    document.head.appendChild(style);
  }
}
